import { useState, useMemo } from 'react';

import { useAppSelector } from 'hooks';

import {
  SimpleGrid,
  Box,
  Collapse,
  Button,
  Icon,
  Flex,
  useColorModeValue,
} from '@chakra-ui/react';

import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

const RatingInformation = ({ index }: { index: number }) => {
  const [isRatingInformationCollapsed, setIsRatingInformationCollapsed] =
    useState(true);

  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const drivingRecord = useAppSelector(
    (state) => state.autoPersonalForm.data.rating_information_driving_record,
  );
  const filteredDrivingRecord = useMemo(() => {
    return drivingRecord.filter(
      // @ts-ignore
      (record) => parseInt(record.auto_nr) === index + 1,
    );
  }, [drivingRecord, index]);

  const discounts = useAppSelector(
    (state) => state.autoPersonalForm.data.rating_information_discounts,
  );
  const filteredDiscounts = useMemo(() => {
    return discounts.filter(
      // @ts-ignore
      (discount) => parseInt(discount.auto_nr) === index + 1,
    );
  }, [discounts, index]);

  return (
    <Box p="20px" py="10px" borderTop="1px solid" borderColor={borderColor}>
      <Flex justifyContent="space-between" alignItems="center">
        <Box fontWeight="bold" fontSize="l">
          Rating Information
        </Box>
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            setIsRatingInformationCollapsed(!isRatingInformationCollapsed)
          }
          rightIcon={
            <Icon
              as={
                isRatingInformationCollapsed ? ChevronDownIcon : ChevronUpIcon
              }
            />
          }
        >
          {isRatingInformationCollapsed
            ? 'Show Rating Information'
            : 'Hide Rating Information'}
        </Button>
      </Flex>
      <Collapse in={!isRatingInformationCollapsed} animateOpacity>
        <Box bg="gray.200" p={2} fontWeight="500" mt={2} roundedTop={5}>
          Driving Record
        </Box>

        <Box mb={4}>
          <SimpleGrid
            columns={11}
            spacing={2}
            px={2}
            py={1}
            bg="gray.100"
            fontSize="sm"
            alignItems="center"
            borderBottom="1px solid"
            borderColor={borderColor}
          >
            <Box>Auto #</Box>
            <Box>Class</Box>
            <Box>BI</Box>
            <Box>PD</Box>
            <Box>AB</Box>
            <Box>DCPD</Box>
            <Box>Coll/AP</Box>
            <Box>Princ.</Box>
            <Box>Sec.</Box>
            <Box>At-Fault Claim Surcharges</Box>
            <Box>Conviction Surcharges</Box>
          </SimpleGrid>
          {filteredDrivingRecord.length > 0 ? (
            filteredDrivingRecord.map((record, recordIdx) => (
              <SimpleGrid
                key={recordIdx}
                columns={11}
                spacing={2}
                px={2}
                py={2}
                fontSize="sm"
                alignItems="center"
              >
                <Box>{record.auto_nr || '-'}</Box>
                <Box>{record.auto_class || '-'}</Box>
                <Box>{record.bi || '-'}</Box>
                <Box>{record.pd || '-'}</Box>
                <Box>{record.ab || '-'}</Box>
                <Box>{record.dcpd || '-'}</Box>
                <Box>{record.coll_ap || '-'}</Box>
                <Box>{record.driver_principal || '-'}</Box>
                <Box>{record.driver_secondary || '-'}</Box>
                <Box>
                  {record.at_fault_claim_description || '-'}{' '}
                  {record.at_fault_claim_percentage
                    ? `(${record.at_fault_claim_percentage}%)`
                    : ''}
                </Box>
                <Box>
                  {record.conviction_description || '-'}{' '}
                  {record.conviction_percentage
                    ? `(${record.conviction_percentage}%)`
                    : ''}
                </Box>
              </SimpleGrid>
            ))
          ) : (
            <Box p={2} color="gray.500" fontSize="sm">
              No driving record available
            </Box>
          )}
        </Box>

        <Box mb={4}>
          <Box bg="gray.200" p={2} fontWeight="500" mt={2} roundedTop={5}>
            Rate Groups
          </Box>
          <SimpleGrid
            columns={10}
            spacing={2}
            px={2}
            py={3}
            bg="gray.100"
            fontSize="sm"
            alignItems="center"
            borderBottom="1px solid"
            borderColor={borderColor}
          >
            <Box>List Price</Box>
            <Box>Code</Box>
            <Box>AB</Box>
            <Box>DCPD</Box>
            <Box>Coll/AP</Box>
            <Box>Comp/SP</Box>
            <Box>Location</Box>
            <Box>P.C.</Box>
            <Box>Territory</Box>
            <Box></Box>
          </SimpleGrid>
          {filteredDiscounts.map((rateGroup, idx: number) => (
            <>
              <SimpleGrid
                key={idx}
                columns={10}
                spacing={2}
                px={2}
                py={2}
                fontSize="sm"
                alignItems="center"
              >
                <Box>{rateGroup.list_price_new || '-'}</Box>
                <Box>{rateGroup.vehicle_code || '-'}</Box>
                <Box>{rateGroup.ab || '-'}</Box>
                <Box>{rateGroup.dcpd || '-'}</Box>
                <Box>{rateGroup.coll_ap || '-'}</Box>
                <Box>{rateGroup.comp_sp || '-'}</Box>
                <Box>{rateGroup.location || '-'}</Box>
                <Box>{rateGroup.postal_code || '-'}</Box>
                <Box>{rateGroup.territory || '-'}</Box>
                <Box></Box>
              </SimpleGrid>

              <Box ml="60%">
                <Box bg="gray.200" p={2} fontWeight="500" mb={2} roundedTop={5}>
                  Discounts
                </Box>
                <Box>
                  {rateGroup.discounts?.map((discount, discountIdx: number) => (
                    <Flex
                      key={discountIdx}
                      px={2}
                      py={1}
                      fontSize="sm"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box flex="1">{discount.discount_description || '-'}</Box>
                      <Box w="60px" textAlign="right" fontWeight="500">
                        {discount.discount_percentage
                          ? `${discount.discount_percentage}%`
                          : '-'}
                      </Box>
                    </Flex>
                  ))}
                  {!rateGroup.discounts?.length && (
                    <Box px={2} py={1} color="gray.500" fontSize="sm">
                      No discounts for this rate group
                    </Box>
                  )}
                </Box>
                {!filteredDiscounts.length && (
                  <Box p={2} color="gray.500" fontSize="sm">
                    No discounts available
                  </Box>
                )}
              </Box>
            </>
          ))}
          {!filteredDiscounts.length && (
            <Box p={2} color="gray.500" fontSize="sm">
              No rate groups available
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
export default RatingInformation;
